import { t as tr, tk } from "../lib/i18n";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { Alert } from "../components/Form";
import { GAMES, GameCard } from "./belajar/games/GamesHub";
import { Progress, Screen, StudyGate, levelName } from "./belajar/kit";
import { Art, Mascot } from "../ui/Art";
import { Icon } from "../ui/Icon";
import { Banner, StatTile, pastel } from "../ui/kit";

type Home = {
  stats: { units: number; lessonsLearned: number; quizzesTaken: number; gamesPlayed: number };
  years: { level: string; year: number; units: number; unitsReady: number; lessons: number; lessonsDone: number }[];
};

export function StudyBoxPage() {
  return (
    <Screen>
      <StudyGate>
        <Home />
      </StudyGate>
    </Screen>
  );
}

function Home() {
  const [data, setData] = useState<Home | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<Home>("/api/learn/home").then(setData).catch((e) => setError(errorMessage(e)));
  }, []);

  return (
    <>
      <Banner art="cover/belajar-hero" kicker="Jom" title="Belajar" tagline={tr("Nota dan kuiz ikut DSKP dan KSSR.")} />

      {error && <Alert kind="error">{error}</Alert>}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile icon={<Icon name="book" />} value={data?.stats.units ?? "–"} label={tr(tk("Unit"))} tone="pastel-blue" />
        <StatTile icon={<Icon name="cap" />} value={data?.stats.lessonsLearned ?? "–"} label={tr(tk("Pelajaran dipelajari"))} tone="pastel-green" />
        <StatTile icon={<Icon name="star" />} value={data?.stats.quizzesTaken ?? "–"} label={tr(tk("Kuiz dijawab"))} tone="pastel-yellow" />
        <StatTile icon={<Icon name="game" />} value={data?.stats.gamesPlayed ?? "–"} label={tr(tk("Permainan dimainkan"))} tone="pastel-pink" />
      </div>

      <Link to="/belajar/tadika" className="pastel-pink mt-6 grid items-center overflow-hidden rounded-3xl shadow-md transition hover:-translate-y-0.5 sm:grid-cols-[220px_1fr]">
        <Art id="cover/huruf" alt="" className="h-40 w-full object-cover sm:h-full" />
        <span className="block p-4">
          <span className="block text-xs font-bold uppercase tracking-wide text-pink-600">{tr("Tadika · 4–6 tahun")}</span>
          <span className="block text-2xl font-black leading-tight text-slate-900">{tr("Jom Baca")}</span>
          <span className="block text-sm text-slate-600">{tr("8 tahap suku kata: dari a-i-u hingga baca cerita.")}</span>
          <span className="mt-2 inline-block rounded-full bg-brand px-4 py-1.5 text-sm font-extrabold text-white">{tr("Mula")} ›</span>
        </span>
      </Link>

      <h2 className="mb-3 mt-8 text-xl font-black text-slate-900"><Icon name="cap" className="mr-2 inline h-6 w-6 text-brand" />{tr("Pilih tahun anda")}</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(data?.years ?? []).map((y) => {
          const ready = y.unitsReady > 0;
          const body = (
            <div className={`${pastel(y.year - 1)} relative flex h-full items-center gap-3 overflow-hidden rounded-3xl p-3 shadow-md ${ready ? "" : "opacity-60"}`}>
              <Mascot pose={y.year % 2 ? "boy-reading" : "girl-reading"} className="h-20 w-20 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-2xl font-black text-slate-900">{levelName(y.level, y.year)}</p>
                <p className="text-xs text-slate-600">{ready ? `${y.unitsReady} ${tr("unit")}` : tr("Akan datang")}</p>
                {ready && (
                  <>
                    <div className="mt-2"><Progress value={y.lessonsDone} max={y.lessons} tone="bg-emerald-500" /></div>
                    <p className="mt-1 text-xs text-slate-600">{y.lessonsDone}/{y.lessons} {tr("pelajaran")}</p>
                  </>
                )}
              </div>
              {ready && <Icon name="arrow" className="h-6 w-6 shrink-0 text-brand" />}
            </div>
          );
          return ready ? (
            <Link key={`${y.level}${y.year}`} to={`/belajar/${y.level}/${y.year}`}>{body}</Link>
          ) : (
            <div key={`${y.level}${y.year}`} aria-disabled>{body}</div>
          );
        })}
      </div>

      <div className="mb-3 mt-8 flex items-end justify-between">
        <h2 className="text-xl font-black text-slate-900"><Icon name="game" className="mr-2 inline h-6 w-6 text-orange-500" />{tr("Permainan")}</h2>
        <Link to="/belajar/permainan" className="text-sm font-bold text-violet-600">{tr("Lihat semua")} ›</Link>
      </div>
      <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0">
        {GAMES.map((g) => <div key={g.to} className="w-44 shrink-0 snap-start sm:w-auto"><GameCard g={g} /></div>)}
      </div>
    </>
  );
}
